import React, { useState, useEffect } from 'react';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  BarElement, 
  ArcElement,
  Title, 
  Tooltip, 
  Legend,
  Filler
} from 'chart.js';
import { Line, Bar, Doughnut, Pie } from 'react-chartjs-2';
import { 
  UsersIcon, 
  UserPlusIcon,
  MapPinIcon,
  DevicePhoneMobileIcon,
  EnvelopeIcon,
  BellIcon,
  FunnelIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import { getFirestore, collection, getDocs, query, orderBy, where } from 'firebase/firestore';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const UserAnalytics = () => {
  const [userData, setUserData] = useState({
    users: [],
    analytics: {
      growth: { labels: [], datasets: [] },
      byCity: { labels: [], datasets: [] },
      byState: { labels: [], datasets: [] },
      byType: { labels: [], datasets: [] },
      fcmStatus: { labels: [], datasets: [] }
    },
    kpis: {
      totalUsers: 0,
      newUsers30Days: 0,
      cumulativeUsers: 0,
      fcmEnabled: 0,
      validPhone: 0,
      gmailUsers: 0
    }
  });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    dateRange: '30',
    city: '',
    type: ''
  });
  const [cities, setCities] = useState([]);

  useEffect(() => {
    loadUserData();
    loadCities();
  }, [filters]);

  const loadCities = async () => {
    try {
      const db = getFirestore();
      const usersRef = collection(db, 'users');
      const querySnapshot = await getDocs(usersRef);
      
      // Extract unique cities
      const citySet = new Set();
      querySnapshot.docs.forEach(doc => {
        const userData = doc.data();
        if (userData.city && userData.city.trim()) {
          citySet.add(userData.city.trim());
        }
      });
      
      const uniqueCities = Array.from(citySet).sort();
      setCities(uniqueCities);
    } catch (error) {
      console.error('Error loading cities:', error);
    }
  };

  const loadUserData = async () => {
    setLoading(true);
    try {
      const db = getFirestore();
      const usersRef = collection(db, 'users');
      
      // Get all users first, then filter in memory for complex queries
      const querySnapshot = await getDocs(usersRef);
      let users = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Apply filters
      if (filters.city) {
        users = users.filter(user => user.city === filters.city);
      }
      
      if (filters.type) {
        if (filters.type === 'Partner') {
          users = users.filter(user => user.type === 'Partner');
        } else if (filters.type === 'User/Owner') {
          // User/Owner means type is not defined or not 'Partner'
          users = users.filter(user => !user.type || user.type !== 'Partner');
        }
      }

      const processedData = processUserAnalytics(users, filters.dateRange);
      setUserData(processedData);
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const processUserAnalytics = (users, dateRange) => {
    let filteredUsers = users;
    let dateLabels = [];
    
    if (dateRange !== 'all') {
      const days = parseInt(dateRange);
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      // Filter users by date range
      filteredUsers = users.filter(user => {
        const userDate = new Date(user.created?.toDate?.() || user.created);
        return userDate >= startDate && userDate <= endDate;
      });

      // Generate date labels
      for (let i = days - 1; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        dateLabels.push(date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
      }
    } else {
      // For "all time", use all users and generate labels for the last 30 days for the chart
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 30);
      
      for (let i = 29; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        dateLabels.push(date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
      }
    }

    // Process user growth data
    const usersByDate = {};
    const cumulativeUsers = {};
    let cumulative = 0;

    filteredUsers.forEach(user => {
      const date = new Date(user.created?.toDate?.() || user.created);
      const dateKey = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      
      usersByDate[dateKey] = (usersByDate[dateKey] || 0) + 1;
      cumulative += 1;
      cumulativeUsers[dateKey] = cumulative;
    });

    // Process location data
    const usersByCity = {};
    const usersByState = {};
    const usersByType = {};
    let fcmEnabled = 0;
    let validPhone = 0;
    let gmailUsers = 0;

    filteredUsers.forEach(user => {
      // City breakdown
      const city = user.city || 'Unknown';
      usersByCity[city] = (usersByCity[city] || 0) + 1;

      // State breakdown
      const state = user.state || 'Unknown';
      usersByState[state] = (usersByState[state] || 0) + 1;

      // Type breakdown
      const type = user.type || 'Unknown';
      usersByType[type] = (usersByType[type] || 0) + 1;

      // FCM token check
      if (user.fcmtoken && user.fcmtoken.trim() !== '') {
        fcmEnabled++;
      }

      // Phone validation (starts with +91)
      if (user.phone && user.phone.startsWith('+91')) {
        validPhone++;
      }

      // Gmail users
      if (user.email && user.email.includes('@gmail.com')) {
        gmailUsers++;
      }
    });

    // Generate chart datasets
    const growthData = dateLabels.map(label => usersByDate[label] || 0);
    const cumulativeData = dateLabels.map(label => cumulativeUsers[label] || 0);

    return {
      users: filteredUsers,
      analytics: {
        growth: {
          labels: dateLabels,
          datasets: [
            {
              label: 'New Users',
              data: growthData,
              borderColor: 'rgb(59, 130, 246)',
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              fill: true,
              tension: 0.4,
              yAxisID: 'y'
            },
            {
              label: 'Cumulative Users',
              data: cumulativeData,
              borderColor: 'rgb(34, 197, 94)',
              backgroundColor: 'rgba(34, 197, 94, 0.1)',
              fill: false,
              tension: 0.4,
              yAxisID: 'y1'
            }
          ]
        },
        byCity: {
          labels: Object.keys(usersByCity).slice(0, 10), // Top 10 cities
          datasets: [{
            label: 'Users by City',
            data: Object.values(usersByCity).slice(0, 10),
            backgroundColor: 'rgba(99, 102, 241, 0.8)',
            borderColor: 'rgb(99, 102, 241)',
            borderWidth: 1
          }]
        },
        byState: {
          labels: Object.keys(usersByState),
          datasets: [{
            data: Object.values(usersByState),
            backgroundColor: [
              'rgba(239, 68, 68, 0.8)',
              'rgba(34, 197, 94, 0.8)',
              'rgba(59, 130, 246, 0.8)',
              'rgba(245, 158, 11, 0.8)',
              'rgba(168, 85, 247, 0.8)',
              'rgba(236, 72, 153, 0.8)',
              'rgba(6, 182, 212, 0.8)',
              'rgba(34, 197, 94, 0.8)'
            ],
            borderColor: [
              'rgb(239, 68, 68)',
              'rgb(34, 197, 94)',
              'rgb(59, 130, 246)',
              'rgb(245, 158, 11)',
              'rgb(168, 85, 247)',
              'rgb(236, 72, 153)',
              'rgb(6, 182, 212)',
              'rgb(34, 197, 94)'
            ],
            borderWidth: 2
          }]
        },
        byType: {
          labels: Object.keys(usersByType),
          datasets: [{
            data: Object.values(usersByType),
            backgroundColor: [
              'rgba(59, 130, 246, 0.8)',
              'rgba(34, 197, 94, 0.8)',
              'rgba(245, 158, 11, 0.8)',
              'rgba(239, 68, 68, 0.8)',
              'rgba(168, 85, 247, 0.8)'
            ],
            borderColor: [
              'rgb(59, 130, 246)',
              'rgb(34, 197, 94)',
              'rgb(245, 158, 11)',
              'rgb(239, 68, 68)',
              'rgb(168, 85, 247)'
            ],
            borderWidth: 2
          }]
        },
        fcmStatus: {
          labels: ['FCM Enabled', 'FCM Disabled'],
          datasets: [{
            data: [fcmEnabled, filteredUsers.length - fcmEnabled],
            backgroundColor: [
              'rgba(34, 197, 94, 0.8)',
              'rgba(156, 163, 175, 0.8)'
            ],
            borderColor: [
              'rgb(34, 197, 94)',
              'rgb(156, 163, 175)'
            ],
            borderWidth: 2
          }]
        }
      },
      kpis: {
        totalUsers: filteredUsers.length,
        newUsers30Days: growthData.reduce((sum, val) => sum + val, 0),
        cumulativeUsers: cumulativeData[cumulativeData.length - 1] || 0,
        fcmEnabled,
        validPhone,
        gmailUsers
      }
    };
  };

  const KPICard = ({ title, value, icon: Icon, color, subtitle }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {subtitle && (
            <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
          )}
        </div>
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );

  const ChartCard = ({ title, children, className = "" }) => (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-6 ${className}`}>
      <h3 className="text-lg font-semibold text-gray-800 mb-4">{title}</h3>
      {children}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">User Analytics</h1>
          <p className="text-gray-600">Comprehensive insights into user growth, location, and engagement</p>
        </div>
        
        {/* Filters */}
        <div className="flex items-center space-x-4 mt-4 sm:mt-0">
          <div className="flex items-center space-x-2">
            <FunnelIcon className="w-5 h-5 text-gray-500" />
            <select
              value={filters.dateRange}
              onChange={(e) => setFilters(prev => ({ ...prev, dateRange: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            >
              <option value="all">All time</option>
              <option value="1">Today</option>
              <option value="2">Last 2 days</option>
              <option value="7">Last 7 days</option>
              <option value="14">Last 14 days</option>
              <option value="30">Last 30 days</option>
              <option value="60">Last 60 days</option>
              <option value="90">Last 90 days</option>
              <option value="180">Last 6 months</option>
              <option value="365">Last year</option>
            </select>
          </div>
          
          <select
            value={filters.city}
            onChange={(e) => setFilters(prev => ({ ...prev, city: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          >
            <option value="">All Cities</option>
            {cities.map(city => (
              <option key={city} value={city}>{city}</option>
            ))}
          </select>
          
          <select
            value={filters.type}
            onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          >
            <option value="">All Types</option>
            <option value="Partner">Partner</option>
            <option value="User/Owner">User/Owner</option>
          </select>
        </div>
      </div>

      {/* Row 1: User KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KPICard
          title="Total Users"
          value={userData.kpis.totalUsers.toLocaleString()}
          icon={UsersIcon}
          color="bg-blue-500"
          subtitle="In selected period"
        />
        <KPICard
          title="New Users"
          value={userData.kpis.newUsers30Days.toLocaleString()}
          icon={UserPlusIcon}
          color="bg-green-500"
          subtitle="Last 30 days"
        />
        <KPICard
          title="FCM Enabled"
          value={`${userData.kpis.fcmEnabled} (${((userData.kpis.fcmEnabled / userData.kpis.totalUsers) * 100).toFixed(1)}%)`}
          icon={BellIcon}
          color="bg-purple-500"
          subtitle="Notification ready"
        />
        <KPICard
          title="Valid Phone"
          value={`${userData.kpis.validPhone} (${((userData.kpis.validPhone / userData.kpis.totalUsers) * 100).toFixed(1)}%)`}
          icon={DevicePhoneMobileIcon}
          color="bg-indigo-500"
          subtitle="+91 format"
        />
      </div>

      {/* Row 2: Growth and User Type Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Growth Chart */}
        <ChartCard title="User Growth Trend">
          <Line
            data={userData.analytics.growth}
            options={{
              responsive: true,
              interaction: {
                mode: 'index',
                intersect: false,
              },
              plugins: {
                legend: {
                  position: 'top',
                },
              },
              scales: {
                y: {
                  type: 'linear',
                  display: true,
                  position: 'left',
                  title: {
                    display: true,
                    text: 'New Users'
                  }
                },
                y1: {
                  type: 'linear',
                  display: true,
                  position: 'right',
                  title: {
                    display: true,
                    text: 'Cumulative Users'
                  },
                  grid: {
                    drawOnChartArea: false,
                  },
                },
              },
            }}
          />
        </ChartCard>

        {/* User Type Distribution */}
        <ChartCard title="User Type Distribution">
          <Doughnut
            data={userData.analytics.byType}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  position: 'bottom',
                },
              },
            }}
          />
        </ChartCard>
      </div>

      {/* Row 3: Location and Device Readiness */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top Cities */}
        <ChartCard title="Top Cities by Users">
          <Bar
            data={userData.analytics.byCity}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  display: false,
                },
              },
              scales: {
                y: {
                  beginAtZero: true,
                },
              },
            }}
          />
        </ChartCard>

        {/* State Distribution */}
        <ChartCard title="Users by State">
          <Pie
            data={userData.analytics.byState}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  position: 'bottom',
                },
              },
            }}
          />
        </ChartCard>

        {/* FCM Status */}
        <ChartCard title="Notification Readiness">
          <Doughnut
            data={userData.analytics.fcmStatus}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  position: 'bottom',
                },
              },
            }}
          />
        </ChartCard>
      </div>

      {/* Additional Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <ChartCard title="Contact Health">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Valid Phone Numbers</span>
              <span className="font-semibold text-green-600">
                {userData.kpis.validPhone} / {userData.kpis.totalUsers}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Gmail Users</span>
              <span className="font-semibold text-blue-600">
                {userData.kpis.gmailUsers} / {userData.kpis.totalUsers}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">FCM Enabled</span>
              <span className="font-semibold text-purple-600">
                {userData.kpis.fcmEnabled} / {userData.kpis.totalUsers}
              </span>
            </div>
          </div>
        </ChartCard>

        <ChartCard title="Growth Metrics">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Cumulative Users</span>
              <span className="font-semibold text-indigo-600">
                {userData.kpis.cumulativeUsers.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">New Users (30d)</span>
              <span className="font-semibold text-green-600">
                {userData.kpis.newUsers30Days.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Growth Rate</span>
              <span className="font-semibold text-orange-600">
                {((userData.kpis.newUsers30Days / userData.kpis.cumulativeUsers) * 100).toFixed(1)}%
              </span>
            </div>
          </div>
        </ChartCard>

        <ChartCard title="Data Quality">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Complete Profiles</span>
              <span className="font-semibold text-green-600">
                {Math.round((userData.kpis.validPhone / userData.kpis.totalUsers) * 100)}%
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Notification Ready</span>
              <span className="font-semibold text-blue-600">
                {Math.round((userData.kpis.fcmEnabled / userData.kpis.totalUsers) * 100)}%
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Email Quality</span>
              <span className="font-semibold text-purple-600">
                {Math.round((userData.kpis.gmailUsers / userData.kpis.totalUsers) * 100)}%
              </span>
            </div>
          </div>
        </ChartCard>
      </div>
    </div>
  );
};

export default UserAnalytics; 